import React, { useEffect, useId, useRef, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

/**
 * Lista rozwijana w stylu platformy zamiast `<select>` przeglądarki (prośba właściciela 2026-10-12, „unowocześnij
 * selektory”): przycisk z wartością i strzałką, pod nim lista z zaznaczeniem, grupami („Z Twoich preferencji”)
 * i opcjonalnym opisem albo obrazkiem przy pozycji. Klawiatura: strzałki, Enter, Escape, pierwsze litery.
 *
 * `opcje`: [{ value, label, opis?, grupa?, obrazek? }]; kolejność grup jak w tablicy. `wartosc` to `value`
 * albo '' (wtedy `pusty`). `pustaOpcja` (tekst) dodaje na górze pozycję z wartością '' (np. „Dowolna seria”).
 */
const Lista = ({ wartosc, opcje, onZmiana, etykieta, pusty = 'Wybierz…', pustaOpcja = null, disabled = false, maly = false, className = '' }) => {
  const id = useId()
  const [otwarta, setOtwarta] = useState(false)
  const [aktywny, setAktywny] = useState(0)
  const ref = useRef(null)
  const lista = useRef(null)
  const pozycje = pustaOpcja != null ? [{ value: '', label: pustaOpcja }, ...opcje] : opcje
  const wybrana = pozycje.find((o) => String(o.value) === String(wartosc ?? ''))

  useEffect(() => {
    if (!otwarta) return undefined
    const poza = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarta(false) }
    document.addEventListener('mousedown', poza)
    lista.current?.focus()
    lista.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' })
    return () => document.removeEventListener('mousedown', poza)
  }, [otwarta])

  const otworz = () => {
    if (disabled) return
    setAktywny(Math.max(0, pozycje.indexOf(wybrana)))
    setOtwarta(true)
  }
  const wybierz = (o) => {
    setOtwarta(false)
    if (String(o.value) !== String(wartosc ?? '')) onZmiana(o.value)
  }
  const klawisz = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') { setOtwarta(false); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktywny((i) => Math.min(pozycje.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktywny((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (pozycje[aktywny]) wybierz(pozycje[aktywny]) }
    else if (e.key.length === 1) {
      const i = pozycje.findIndex((o) => String(o.label).toLowerCase().startsWith(e.key.toLowerCase()))
      if (i >= 0) setAktywny(i)
    }
  }
  useEffect(() => {
    if (otwarta) lista.current?.children[aktywnyIndeksDom(pozycje, aktywny)]?.scrollIntoView?.({ block: 'nearest' })
  }, [aktywny, otwarta]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <button type="button" id={id} onClick={() => (otwarta ? setOtwarta(false) : otworz())} disabled={disabled}
              aria-haspopup="listbox" aria-expanded={otwarta} aria-label={etykieta}
              onKeyDown={(e) => { if (!otwarta && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); otworz() } }}
              className={`flex w-full min-w-0 items-center gap-2 rounded-lg border bg-white text-left shadow-sm transition
                          ${maly ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'}
                          ${otwarta ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}
                          focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 disabled:shadow-none`}>
        {wybrana?.obrazek && <img src={wybrana.obrazek} alt="" className="h-6 w-6 flex-shrink-0 rounded object-cover" />}
        <span className={`min-w-0 flex-1 truncate ${wybrana && wybrana.value !== '' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
          {wybrana ? wybrana.label : pusty}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
      </button>
      {otwarta && (
        <ul ref={lista} role="listbox" tabIndex={-1} aria-labelledby={id} onKeyDown={klawisz}
            className="absolute inset-x-0 z-40 mt-1 max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-1 shadow-lg focus:outline-none [scrollbar-width:thin]">
          {pozycje.map((o, i) => {
            const nowaGrupa = o.grupa && o.grupa !== pozycje[i - 1]?.grupa
            const zaznaczona = String(o.value) === String(wartosc ?? '')
            return (
              <React.Fragment key={`${o.grupa || ''}:${o.value}`}>
                {nowaGrupa && (
                  <li role="presentation" className={`px-2.5 pb-1 text-[11px] font-semibold text-gray-500 ${i > 0 ? 'mt-1 border-t border-gray-100 pt-2' : 'pt-1'}`}>{o.grupa}</li>
                )}
                <li role="option" aria-selected={zaznaczona} onMouseEnter={() => setAktywny(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => wybierz(o)}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm ${i === aktywny ? 'bg-blue-50' : ''}`}>
                  {o.obrazek && <img src={o.obrazek} alt="" className="h-8 w-8 flex-shrink-0 rounded-md border border-gray-100 object-cover" />}
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate ${zaznaczona ? 'font-semibold text-blue-800' : 'text-gray-800'}`}>{o.label}</span>
                    {o.opis && <span className="block truncate text-[11px] text-gray-500">{o.opis}</span>}
                  </span>
                  {zaznaczona && <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />}
                </li>
              </React.Fragment>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Indeks elementu w DOM dla pozycji `i` (nagłówki grup są osobnymi <li>).
const aktywnyIndeksDom = (pozycje, i) => {
  let dom = 0
  for (let k = 0; k <= i && k < pozycje.length; k++) {
    if (pozycje[k].grupa && pozycje[k].grupa !== pozycje[k - 1]?.grupa) dom++
    if (k < i) dom++
  }
  return dom
}

export default Lista
